import React, { useState, useEffect } from 'react';
import { 
    Card, 
    Button, 
    Form, 
    Input, 
    message, 
    Typography, 
    Row, 
    Col, 
    Tabs, 
    Space, 
    Alert, 
    Spin,
    Tooltip,
    Tag,
    Result,
    Popconfirm,
    List
} from 'antd';
import { 
    ApiOutlined, 
    SafetyOutlined, 
    CloudOutlined, 
    LinkOutlined,
    SaveOutlined,
    ReloadOutlined,
    LockOutlined,
    CheckCircleOutlined,
    GlobalOutlined,
    ExperimentOutlined,
    DatabaseOutlined
} from '@ant-design/icons';
import { getAllApiConfigs, updateApiConfig, getDbEnvironments, switchDbEnvironment } from '../api';
import { setBackendUrl, getCurrentBackendUrl, resetBackendUrl, hasLocalBackendOverride, getConfig, refreshConfig } from '../../../services/api';
import { usePermissions } from '../../../hooks/usePermissions';
import DigilockerWebhookCard from '../components/DigilockerWebhookCard';
import ESignWebhookCard from '../components/ESignWebhookCard';

const { Title, Text, Paragraph } = Typography;

const ApiConfigPage = () => {
    const { canEditModule, canViewField, canEditField } = usePermissions();
    const canEdit = canEditModule('system');
    const canViewApiConfig = canViewField('system', 'apiConfig');
    const canEditApiConfig = canEditField('system', 'apiConfig');
    
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState({});
    const [configs, setConfigs] = useState({});
    const [currentBackendUrl, setCurrentBackendUrl] = useState(getCurrentBackendUrl());
    const [systemConfig, setSystemConfig] = useState(null);
    const [hasOverride, setHasOverride] = useState(hasLocalBackendOverride());
    const [testingConnection, setTestingConnection] = useState(false);
    // Database environments are described by NAME + masked cluster only.
    // The browser never receives a connection string.
    const [dbEnvironments, setDbEnvironments] = useState([]);
    const [activeDbEnv, setActiveDbEnv] = useState(null);
    const [dbEnvLoading, setDbEnvLoading] = useState(false);
    const [switchingDb, setSwitchingDb] = useState(null);
    const [forms] = useState({
        leegality: Form.useForm()[0],
        imagekit: Form.useForm()[0],
        sandbox: Form.useForm()[0],
        webhook: Form.useForm()[0],
        backend: Form.useForm()[0],
        frontend: Form.useForm()[0]
    });

    useEffect(() => {
        fetchConfigs();
        loadSystemConfig();
        loadDbEnvironments();
    }, []);

    const loadDbEnvironments = async () => {
        setDbEnvLoading(true);
        try {
            const data = await getDbEnvironments();
            setDbEnvironments(data.environments || []);
            setActiveDbEnv(data.activeEnv || null);
        } catch (error) {
            console.error('Failed to load database environments:', error);
        } finally {
            setDbEnvLoading(false);
        }
    };

    const handleSwitchDb = async (envKey) => {
        setSwitchingDb(envKey);
        try {
            const data = await switchDbEnvironment(envKey);
            setDbEnvironments(data.environments || []);
            setActiveDbEnv(data.activeEnv || null);

            if (data.switched) {
                message.success(data.message, 6);
                // The signing key was rotated, so this session is no longer
                // valid against the new database. Send the admin back to login.
                setTimeout(() => { window.location.href = '/login'; }, 2500);
            } else {
                message.info(data.message);
            }
        } catch (error) {
            message.error(error?.response?.data?.message || 'Failed to switch database environment');
            loadDbEnvironments();
        } finally {
            setSwitchingDb(null);
        }
    };

    const loadSystemConfig = async () => {
        try {
            const config = getConfig();
            setSystemConfig(config);
            
            // Set form values for frontend config.
            // Only the public bootstrap fields come from ConfigService — the DB
            // URIs are deliberately absent from /system/frontend-config and are
            // filled in by fetchConfigs() via the auth-gated /system/api-config.
            if (forms.frontend && config) {
                forms.frontend.setFieldsValue({
                    backendUrl: config.backendUrl || '',
                    devBackendUrl: config.devBackendUrl || 'http://localhost:5050',
                    allowLocalOverride: config.allowLocalOverride
                });
            }
            
            // Set backend override form
            forms.backend.setFieldsValue({ backendUrl: currentBackendUrl });
        } catch (error) {
            console.error('Failed to load system config:', error);
        }
    };

    const fetchConfigs = async () => {
        setLoading(true);
        try {
            const data = await getAllApiConfigs();
            setConfigs(data.configs || {});
            
            // Set form values
            Object.keys(data.configs || {}).forEach(category => {
                if (forms[category]) {
                    forms[category].setFieldsValue(data.configs[category].config);
                }
            });
        } catch (error) {
            message.error('Failed to load API configurations');
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async (category) => {
        try {
            const values = await forms[category].validateFields();

            // Saving the frontend config no longer touches the database at all.
            // Database switching is a separate, name-only action (handleSwitchDb).

            setSaving({ ...saving, [category]: true });
            
            await updateApiConfig(category, values);
            message.success(`${category.charAt(0).toUpperCase() + category.slice(1)} configuration saved successfully!`);
            
            // If it's frontend config, refresh the system configuration
            if (category === 'frontend') {
                setTimeout(async () => {
                    const refreshed = await refreshConfig();
                    if (refreshed) {
                        await loadSystemConfig();
                        setCurrentBackendUrl(getCurrentBackendUrl());
                        message.success('System configuration applied! All users will use the new settings on their next page load.');
                    }
                }, 1000);
            } else {
                // Refresh to get updated masked values
                await fetchConfigs();
            }
        } catch (error) {
            if (error.errorFields) {
                message.error('Please fill in all required fields');
            } else if (error.response?.data?.message) {
                message.error(error.response.data.message);
            } else {
                message.error(`Failed to save ${category} configuration`);
            }
        } finally {
            setSaving({ ...saving, [category]: false });
        }
    };

    const handleBackendUrlUpdate = async () => {
        try {
            const values = await forms.backend.validateFields();
            const newUrl = values.backendUrl.trim();
            
            setSaving({ ...saving, backend: true });
            
            // Check if local override is allowed
            if (!systemConfig?.allowLocalOverride) {
                message.warning('Local backend URL override is disabled by system configuration. Please contact your administrator.');
                return;
            }
            
            // Set local override
            const success = setBackendUrl(newUrl);
            if (success) {
                setCurrentBackendUrl(newUrl);
                setHasOverride(true);
                message.success('Local backend URL override applied! This only affects your browser session.');
            } else {
                message.error('Failed to set backend URL override');
            }
        } catch (error) {
            if (error.errorFields) {
                message.error('Please enter a valid URL');
            } else {
                message.error('Failed to update backend URL');
            }
        } finally {
            setSaving({ ...saving, backend: false });
        }
    };

    const handleTestConnection = async () => {
        try {
            const values = await forms.backend.validateFields();
            const testUrl = values.backendUrl.trim();
            
            setTestingConnection(true);
            
            // Try to make a simple API call
            const response = await fetch(`${testUrl}/api/v1/system/frontend-config`, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' }
            });
            
            if (response.ok) {
                message.success('✅ Connection successful! Backend is reachable.');
            } else {
                message.warning('Backend URL is reachable but may not be the correct API endpoint.');
            }
            
        } catch (error) {
            message.error('❌ Connection failed: ' + error.message);
        } finally {
            setTestingConnection(false);
        }
    };

    const handleResetBackendUrl = () => {
        resetBackendUrl();
        const serverUrl = getCurrentBackendUrl();
        setCurrentBackendUrl(serverUrl);
        setHasOverride(false);
        forms.backend.setFieldsValue({ backendUrl: serverUrl });
        message.info('Local backend URL override removed. Now using system configuration.');
    };

    const renderSensitiveField = (fieldName, label, placeholder, config) => {
        const hasValue = config?.config?.[`${fieldName}_hasValue`];
        
        return (
            <Form.Item
                name={fieldName}
                label={
                    <Space>
                        {label}
                        {hasValue && (
                            <Tooltip title="This field has a stored value">
                                <Tag color="green" icon={<CheckCircleOutlined />}>Configured</Tag>
                            </Tooltip>
                        )}
                    </Space>
                }
            >
                <Input.Password
                    placeholder={placeholder}
                    prefix={<LockOutlined />}
                    disabled={!canEditApiConfig}
                />
            </Form.Item>
        );
    };

    const renderTextField = (fieldName, label, placeholder) => (
        <Form.Item name={fieldName} label={label}>
            <Input placeholder={placeholder} disabled={!canEditApiConfig} />
        </Form.Item>
    );

    if (loading) {
        return (
            <div style={{ textAlign: 'center', padding: '100px' }}>
                <Spin size="large" tip="Loading API Configurations..." />
            </div>
        );
    }

    const tabItems = [
        {
            key: 'leegality',
            label: (
                <span>
                    <SafetyOutlined />
                    Leegality (eSign)
                </span>
            ),
            children: (
                <Card>
                    <Alert
                        message="Leegality eSign Integration"
                        description="Configure your Leegality API credentials for electronic signature functionality. These credentials are encrypted and stored securely."
                        type="info"
                        showIcon
                        style={{ marginBottom: 24 }}
                    />
                    
                    <Form form={forms.leegality} layout="vertical">
                        <Row gutter={16}>
                            <Col span={12}>
                                {renderSensitiveField('apiKey', 'API Key', 'Enter Leegality API Key', configs.leegality)}
                            </Col>
                            <Col span={12}>
                                {renderSensitiveField('privateKey', 'Private Key', 'Enter Private Key', configs.leegality)}
                            </Col>
                        </Row>
                        
                        <Row gutter={16}>
                            <Col span={12}>
                                {renderSensitiveField('privateSalt', 'Private Salt', 'Enter Private Salt', configs.leegality)}
                            </Col>
                            <Col span={12}>
                                {renderTextField('profileId', 'Profile ID', 'Enter Profile ID')}
                            </Col>
                        </Row>
                        
                        <Row gutter={16}>
                            <Col span={12}>
                                {renderTextField('baseUrl', 'Base URL', 'https://app1.leegality.com')}
                            </Col>
                            <Col span={6}>
                                {renderTextField('signApiVersion', 'Sign API Version', '3.0')}
                            </Col>
                            <Col span={6}>
                                {renderTextField('detailsApiVersion', 'Details API Version', '3.3')}
                            </Col>
                        </Row>
                        
                        <Space style={{ marginTop: 16 }}>
                            <Button 
                                type="primary" 
                                icon={<SaveOutlined />}
                                onClick={() => handleSave('leegality')}
                                loading={saving.leegality}
                                disabled={!canEditApiConfig}
                            >
                                Save Leegality Config
                            </Button>
                        </Space>
                    </Form>
                </Card>
            )
        },
        {
            key: 'imagekit',
            label: (
                <span>
                    <CloudOutlined />
                    ImageKit
                </span>
            ),
            children: (
                <Card>
                    <Alert
                        message="ImageKit Media Storage"
                        description="Configure your ImageKit credentials for image upload and storage. The private key is encrypted."
                        type="info"
                        showIcon
                        style={{ marginBottom: 24 }}
                    />
                    
                    <Form form={forms.imagekit} layout="vertical">
                        <Row gutter={16}>
                            <Col span={12}>
                                {renderTextField('publicKey', 'Public Key', 'Enter ImageKit Public Key')}
                            </Col>
                            <Col span={12}>
                                {renderSensitiveField('privateKey', 'Private Key', 'Enter ImageKit Private Key', configs.imagekit)}
                            </Col>
                        </Row>
                        
                        <Row gutter={16}>
                            <Col span={12}>
                                {renderTextField('urlEndpoint', 'URL Endpoint', 'https://ik.imagekit.io/your-id')}
                            </Col>
                            <Col span={6}>
                                {renderTextField('id', 'ImageKit ID', 'your-imagekit-id')}
                            </Col>
                            <Col span={6}>
                                {renderTextField('uploadLimit', 'Upload Limit', '10mb')}
                            </Col>
                        </Row>
                        
                        <Space style={{ marginTop: 16 }}>
                            <Button 
                                type="primary" 
                                icon={<SaveOutlined />}
                                onClick={() => handleSave('imagekit')}
                                loading={saving.imagekit}
                                disabled={!canEditApiConfig}
                            >
                                Save ImageKit Config
                            </Button>
                        </Space>
                    </Form>
                </Card>
            )
        },
        {
            key: 'sandbox',
            label: (
                <span>
                    <ApiOutlined />
                    Sandbox (eKYC)
                </span>
            ),
            children: (
                <Card>
                    <Alert
                        message="Sandbox eKYC Integration"
                        description="Configure your Sandbox API credentials for electronic KYC verification. Both API key and secret are encrypted."
                        type="info"
                        showIcon
                        style={{ marginBottom: 24 }}
                    />
                    
                    <Form form={forms.sandbox} layout="vertical">
                        <Row gutter={16}>
                            <Col span={12}>
                                {renderSensitiveField('apiKey', 'API Key', 'Enter Sandbox API Key', configs.sandbox)}
                            </Col>
                            <Col span={12}>
                                {renderSensitiveField('secretKey', 'Secret Key', 'Enter Sandbox Secret Key', configs.sandbox)}
                            </Col>
                        </Row>
                        
                        <Row gutter={16}>
                            <Col span={12}>
                                {renderTextField('baseUrl', 'Base URL', 'https://api.sandbox.co.in')}
                            </Col>
                            <Col span={12}>
                                {renderSensitiveField('webhookSecret', 'Webhook Secret', 'Same secret as in Sandbox Console → Webhooks', configs.sandbox)}
                            </Col>
                        </Row>
                        
                        <Space style={{ marginTop: 16 }}>
                            <Button 
                                type="primary" 
                                icon={<SaveOutlined />}
                                onClick={() => handleSave('sandbox')}
                                loading={saving.sandbox}
                                disabled={!canEditApiConfig}
                            >
                                Save Sandbox Config
                            </Button>
                        </Space>
                    </Form>
                    <DigilockerWebhookCard canEdit={canEditApiConfig} />
                </Card>
            )
        },
        {
            key: 'webhook',
            label: (
                <span>
                    <LinkOutlined />
                    Webhook
                </span>
            ),
            children: (
                <Card>
                    <Alert
                        message="Webhook Configuration"
                        description="Configure the base URL for webhook callbacks. This URL is used by external services to send notifications back to your system."
                        type="info"
                        showIcon
                        style={{ marginBottom: 24 }}
                    />
                    
                    <Form form={forms.webhook} layout="vertical">
                        <Row gutter={16}>
                            <Col span={16}>
                                {renderTextField('webhookBaseUrl', 'Webhook Base URL', 'https://your-api-domain.com')}
                            </Col>
                        </Row>
                        
                        <Paragraph type="secondary" style={{ marginTop: 8 }}>
                            <Text>This URL will be used for callbacks from:</Text>
                            <ul>
                                <li>Leegality eSign webhook notifications</li>
                                <li>Sandbox DigiLocker webhook + consent redirect (must be HTTPS)</li>
                                <li>Payment gateway callbacks</li>
                                <li>Other external service integrations</li>
                            </ul>
                        </Paragraph>
                        
                        <Space style={{ marginTop: 16 }}>
                            <Button 
                                type="primary" 
                                icon={<SaveOutlined />}
                                onClick={() => handleSave('webhook')}
                                loading={saving.webhook}
                                disabled={!canEditApiConfig}
                            >
                                Save Webhook Config
                            </Button>
                        </Space>
                    </Form>
                    <ESignWebhookCard canEdit={canEditApiConfig} />
                </Card>
            )
        },
        {
            key: 'frontend',
            label: (
                <span>
                    <GlobalOutlined />
                    System Configuration
                </span>
            ),
            children: (
                <Card>
                    <Alert
                        message="System-Wide Frontend Configuration"
                        description="Backend URL is now environment-aware: production clients (crm.auriksha.com) use the Production Backend URL, while localhost clients use the Dev Backend URL. The two never cross, so changing the dev URL can never break production."
                        type="info"
                        showIcon
                        style={{ marginBottom: 24 }}
                    />
                    
                    <Row gutter={16}>
                        <Col span={12}>
                            <Card size="small" title="Current System Config" type="inner">
                                <Space direction="vertical" style={{ width: '100%' }}>
                                    <div>
                                        <Text type="secondary">Production Backend URL:</Text>
                                        <br />
                                        <Tag color="blue">{systemConfig?.backendUrl || 'Not configured'}</Tag>
                                    </div>
                                    <div>
                                        <Text type="secondary">Dev Backend URL (localhost):</Text>
                                        <br />
                                        <Tag color="purple">{systemConfig?.devBackendUrl || 'http://localhost:5050'}</Tag>
                                    </div>
                                    <div>
                                        <Text type="secondary">Environment:</Text>
                                        <br />
                                        <Tag color={systemConfig?.environment === 'production' ? 'red' : 'orange'}>
                                            {systemConfig?.environment || 'unknown'}
                                        </Tag>
                                    </div>
                                </Space>
                            </Card>
                        </Col>
                        <Col span={12}>
                            <Card size="small" title="Impact" type="inner">
                                <Space direction="vertical" style={{ width: '100%' }}>
                                    <Text type="secondary">
                                        <CheckCircleOutlined style={{ color: 'green', marginRight: 8 }} />
                                        Affects all users system-wide
                                    </Text>
                                    <Text type="secondary">
                                        <CheckCircleOutlined style={{ color: 'green', marginRight: 8 }} />
                                        Applied on next page load
                                    </Text>
                                    <Text type="secondary">
                                        <CheckCircleOutlined style={{ color: 'green', marginRight: 8 }} />
                                        Stored in database
                                    </Text>
                                    <Text type="secondary">
                                        <CheckCircleOutlined style={{ color: 'green', marginRight: 8 }} />
                                        Survives deployments
                                    </Text>
                                </Space>
                            </Card>
                        </Col>
                    </Row>
                    
                    <Form form={forms.frontend} layout="vertical" style={{ marginTop: 24 }}>
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    name="backendUrl"
                                    label="Production Backend URL (System)"
                                    tooltip="Used only by production clients (e.g. crm.auriksha.com). localhost values are rejected. Development machines use the Dev Backend URL below instead."
                                    rules={[
                                        { required: true, message: 'Please enter a backend URL' },
                                        { pattern: /^https?:\/\/.+/, message: 'URL must start with http:// or https://' },
                                        {
                                            validator: (_, value) =>
                                                value && /localhost|127\.0\.0\.1|0\.0\.0\.0|::1/i.test(value)
                                                    ? Promise.reject(new Error('Production URL cannot point to localhost — use the Dev Backend URL field'))
                                                    : Promise.resolve()
                                        }
                                    ]}
                                >
                                    <Input
                                        placeholder="https://api.yourdomain.com"
                                        disabled={!canEditApiConfig}
                                        addonBefore={<GlobalOutlined />}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    name="devBackendUrl"
                                    label="Dev Backend URL (localhost clients)"
                                    tooltip="Used only when the frontend is opened on localhost. Safe to change — it never affects production clients."
                                    rules={[
                                        { pattern: /^https?:\/\/.+/, message: 'URL must start with http:// or https://' }
                                    ]}
                                >
                                    <Input
                                        placeholder="http://localhost:5050"
                                        disabled={!canEditApiConfig}
                                        addonBefore={<ExperimentOutlined />}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={16}>
                            <Col span={24}>
                                <Card
                                    size="small"
                                    title={
                                        <Space>
                                            <span>Active Database</span>
                                            {activeDbEnv && (
                                                <Tag color="green">
                                                    {(dbEnvironments.find((e) => e.key === activeDbEnv) || {}).label || activeDbEnv}
                                                </Tag>
                                            )}
                                        </Space>
                                    }
                                    type="inner"
                                    style={{ marginBottom: 16 }}
                                    extra={
                                        <Button
                                            size="small"
                                            icon={<ReloadOutlined />}
                                            onClick={loadDbEnvironments}
                                            loading={dbEnvLoading}
                                        >
                                            Refresh
                                        </Button>
                                    }
                                >
                                    <Alert
                                        type="info"
                                        showIcon
                                        style={{ marginBottom: 16 }}
                                        message="Connection strings are stored on the server only"
                                        description={
                                            <span>
                                                Each environment below is configured in the server's <Text code>.env</Text> file.
                                                Switching sends only the environment name &mdash; database credentials are never
                                                sent to, stored in, or displayed by this browser. To add or change a connection
                                                string, edit <Text code>.env</Text> on the server and restart it.
                                            </span>
                                        }
                                    />

                                    <List
                                        loading={dbEnvLoading}
                                        dataSource={dbEnvironments}
                                        locale={{ emptyText: 'No database environments configured on the server.' }}
                                        renderItem={(env) => (
                                            <List.Item
                                                actions={[
                                                    env.active ? (
                                                        <Tag color="green" icon={<CheckCircleOutlined />}>Active</Tag>
                                                    ) : !env.configured ? (
                                                        <Tooltip title={`Set ${env.envVar} in the server .env and restart`}>
                                                            <Tag>Not configured</Tag>
                                                        </Tooltip>
                                                    ) : (
                                                        <Popconfirm
                                                            title={`Switch to ${env.label}?`}
                                                            description={
                                                                <div style={{ maxWidth: 320 }}>
                                                                    The entire backend will reconnect to this database and
                                                                    <b> every user will be signed out immediately</b>, including you.
                                                                </div>
                                                            }
                                                            okText="Switch database"
                                                            okButtonProps={{ danger: true }}
                                                            cancelText="Cancel"
                                                            onConfirm={() => handleSwitchDb(env.key)}
                                                            disabled={!canEditApiConfig || Boolean(switchingDb)}
                                                        >
                                                            <Button
                                                                size="small"
                                                                danger
                                                                loading={switchingDb === env.key}
                                                                disabled={!canEditApiConfig || Boolean(switchingDb)}
                                                            >
                                                                Switch
                                                            </Button>
                                                        </Popconfirm>
                                                    )
                                                ]}
                                            >
                                                <List.Item.Meta
                                                    avatar={<DatabaseOutlined style={{ fontSize: 20, marginTop: 4 }} />}
                                                    title={<Text strong>{env.label}</Text>}
                                                    description={
                                                        env.configured ? (
                                                            <Text type="secondary">
                                                                Cluster <Text code>{env.cluster}</Text> &middot;
                                                                database <Text code>{env.database}</Text>
                                                            </Text>
                                                        ) : (
                                                            <Text type="secondary">
                                                                Nothing set for <Text code>{env.envVar}</Text>
                                                            </Text>
                                                        )
                                                    }
                                                />
                                            </List.Item>
                                        )}
                                    />
                                </Card>
                            </Col>
                        </Row>
                        
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item name="allowLocalOverride" valuePropName="checked">
                                    <Space>
                                        <input 
                                            type="checkbox" 
                                            disabled={!canEditApiConfig}
                                        />
                                        <Text>Allow users to set local backend URL overrides</Text>
                                    </Space>
                                </Form.Item>
                            </Col>
                        </Row>
                        
                        <Space style={{ marginTop: 16 }}>
                            <Button 
                                type="primary" 
                                icon={<SaveOutlined />}
                                onClick={() => handleSave('frontend')}
                                loading={saving.frontend}
                                disabled={!canEditApiConfig}
                            >
                                Save System Configuration
                            </Button>
                        </Space>
                    </Form>
                    
                    <Alert
                        message="Important Notes"
                        description={
                            <ul style={{ marginBottom: 0, paddingLeft: 16 }}>
                                <li><strong>System-wide Impact:</strong> This affects all users across all browsers/sessions</li>
                                <li><strong>Deployment Safe:</strong> Configuration persists through code deployments</li>
                                <li><strong>Real-time:</strong> Changes apply when users refresh or navigate</li>
                                <li><strong>Fallback:</strong> If server config fails, users fall back to build-time defaults</li>
                            </ul>
                        }
                        type="warning"
                        showIcon
                        style={{ marginTop: 24 }}
                    />
                </Card>
            )
        },
        {
            key: 'backend',
            label: (
                <span>
                    <ExperimentOutlined />
                    Local Override
                </span>
            ),
            children: (
                <Card>
                    <Alert
                        message={
                            systemConfig?.allowLocalOverride 
                                ? "Local Backend URL Override (This Browser Only)"
                                : "Local Override Disabled"
                        }
                        description={
                            systemConfig?.allowLocalOverride
                                ? "Override the backend URL for testing or development. This only affects your current browser session and does not change the system configuration for other users."
                                : "Local backend URL overrides have been disabled by your system administrator. Contact the administrator to change backend URLs system-wide."
                        }
                        type={systemConfig?.allowLocalOverride ? "info" : "warning"}
                        showIcon
                        style={{ marginBottom: 24 }}
                    />
                    
                    <Row gutter={16}>
                        <Col span={12}>
                            <Card size="small" title="Current Status" type="inner">
                                <Space direction="vertical" style={{ width: '100%' }}>
                                    <div>
                                        <Text type="secondary">Active Backend URL:</Text>
                                        <br />
                                        <Tag 
                                            color={hasOverride ? "orange" : "blue"}
                                            style={{ wordBreak: 'break-all' }}
                                        >
                                            {currentBackendUrl}
                                        </Tag>
                                    </div>
                                    <div>
                                        <Text type="secondary">Status:</Text>
                                        <br />
                                        {hasOverride ? (
                                            <Tag color="orange" icon={<ExperimentOutlined />}>
                                                Local Override Active
                                            </Tag>
                                        ) : (
                                            <Tag color="blue" icon={<GlobalOutlined />}>
                                                Using System Config
                                            </Tag>
                                        )}
                                    </div>
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                        All API calls go to: <code>{currentBackendUrl}/api/v1</code>
                                    </Text>
                                </Space>
                            </Card>
                        </Col>
                        <Col span={12}>
                            <Card size="small" title="Quick Presets" type="inner">
                                <Space direction="vertical" style={{ width: '100%' }}>
                                    <Button 
                                        size="small" 
                                        onClick={() => forms.backend.setFieldsValue({ backendUrl: 'http://localhost:5050' })}
                                        disabled={!systemConfig?.allowLocalOverride}
                                    >
                                        Development (localhost:5050)
                                    </Button>
                                    <Button
                                        size="small"
                                        onClick={() => forms.backend.setFieldsValue({ backendUrl: 'https://api.auriksha.com' })}
                                        disabled={!systemConfig?.allowLocalOverride}
                                    >
                                        Production (api.auriksha.com)
                                    </Button>
                                    <Button 
                                        size="small" 
                                        onClick={() => forms.backend.setFieldsValue({ backendUrl: systemConfig?.backendUrl || '' })}
                                        disabled={!systemConfig?.allowLocalOverride}
                                    >
                                        Use System Default
                                    </Button>
                                </Space>
                            </Card>
                        </Col>
                    </Row>
                    
                    <Form form={forms.backend} layout="vertical" style={{ marginTop: 24 }}>
                        <Row gutter={16}>
                            <Col span={20}>
                                <Form.Item 
                                    name="backendUrl" 
                                    label="Backend URL Override"
                                    rules={[
                                        { required: true, message: 'Please enter a backend URL' },
                                        { pattern: /^https?:\/\/.+/, message: 'URL must start with http:// or https://' }
                                    ]}
                                >
                                    <Input 
                                        placeholder="https://your-backend-domain.com" 
                                        disabled={!systemConfig?.allowLocalOverride}
                                        addonBefore={<ExperimentOutlined />}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                        
                        <Space style={{ marginTop: 16 }}>
                            <Button 
                                type="primary" 
                                icon={<SaveOutlined />}
                                onClick={handleBackendUrlUpdate}
                                loading={saving.backend}
                                disabled={!systemConfig?.allowLocalOverride}
                            >
                                Set Local Override
                            </Button>
                            <Button 
                                icon={<ExperimentOutlined />}
                                onClick={handleTestConnection}
                                loading={testingConnection}
                                disabled={!systemConfig?.allowLocalOverride}
                            >
                                Test Connection
                            </Button>
                            <Button 
                                onClick={handleResetBackendUrl}
                                disabled={!systemConfig?.allowLocalOverride || !hasOverride}
                            >
                                Clear Override
                            </Button>
                        </Space>
                    </Form>
                    
                    {!systemConfig?.allowLocalOverride && (
                        <Alert
                            message="Override Disabled"
                            description="Local backend URL overrides are disabled by system policy. Ask your administrator to enable this feature or change the backend URL system-wide."
                            type="info"
                            showIcon
                            style={{ marginTop: 24 }}
                        />
                    )}
                </Card>
            )
        }
    ];

    if (!canViewApiConfig) {
        return (
            <div style={{ padding: '24px' }}>
                <Result
                    status="403"
                    title="Access Denied"
                    subTitle="You don't have permission to view API Configuration settings."
                    icon={<LockOutlined style={{ fontSize: 48, color: '#ff4d4f' }} />}
                />
            </div>
        );
    }

    return (
        <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
            <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
                <Col>
                    <Title level={2} style={{ margin: 0 }}>
                        <ApiOutlined style={{ marginRight: 12 }} />
                        API Configuration
                    </Title>
                    <Space direction="vertical" style={{ marginTop: 8 }}>
                        <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                            Manage external service API keys and integration settings. Sensitive values are encrypted.
                        </Paragraph>
                        <Space>
                            <Text type="secondary">Active Backend:</Text>
                            {hasOverride ? (
                                <Tag color="orange" icon={<ExperimentOutlined />}>
                                    Local Override: {currentBackendUrl.replace(/^https?:\/\//, '')}
                                </Tag>
                            ) : (
                                <Tag color="processing" icon={<GlobalOutlined />}>
                                    System Config: {currentBackendUrl.replace(/^https?:\/\//, '')}
                                </Tag>
                            )}
                        </Space>
                    </Space>
                </Col>
                <Col>
                    <Button 
                        icon={<ReloadOutlined />} 
                        onClick={fetchConfigs}
                        loading={loading}
                    >
                        Refresh
                    </Button>
                </Col>
            </Row>

            <Alert
                message="Security Notice"
                description="API keys and secrets are encrypted using AES-256 encryption before being stored. Masked values (****) indicate existing credentials - leave unchanged to keep current values."
                type="warning"
                showIcon
                style={{ marginBottom: 24 }}
            />

            <Tabs 
                defaultActiveKey="frontend" 
                items={tabItems}
                type="card"
                size="large"
            />
        </div>
    );
};

export default ApiConfigPage;
